import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { TranslationCoreModule } from '@qtek/libs/translation-core';
import {
  chatFileStatusLabel,
  ChatFileVM,
  fileTypeLabel,
} from '../../../models/chat-file';

/**
 * SD-3016: one attached file, in the composer or on a sent message, with the step it is on and
 * a progress bar while it uploads (by percentage) or is scanned and read (indeterminate).
 * A failed upload offers "Try again"; a file the virus scan blocked doesn't, as it would fail again.
 */
@Component({
  selector: 'qt-ai-chat-file-chip',
  standalone: true,
  imports: [MatIconModule, MatProgressBarModule, TranslationCoreModule],
  template: `
    <span
      class="qt-file-chip"
      [class.qt-file-chip--failed]="file().step === 'failed'">
      <span class="qt-file-chip__row">
        <span class="qt-file-chip__name" [title]="file().name">{{
          file().name
        }}</span>
        <span class="qt-file-chip__type">{{ typeLabel() }}</span>
        @if (canRetry()) {
          <button
            type="button"
            class="qt-file-chip__retry"
            (click)="retry.emit()">
            {{ 'GNR_TRY_AGAIN' | translate }}
          </button>
        }
        @if (removable()) {
          <button
            type="button"
            class="qt-file-chip__remove"
            (click)="remove.emit()"
            [attr.aria-label]="'GNR_REMOVE_ATTACHMENT' | translate">
            <mat-icon>close</mat-icon>
          </button>
        }
      </span>
      @if (statusLabel(); as label) {
        <span
          class="qt-file-chip__status"
          [attr.role]="file().step === 'failed' ? 'alert' : 'status'">
          {{ label }}
        </span>
      }
      @if (busy()) {
        <mat-progress-bar
          class="qt-file-chip__progress"
          [mode]="file().step === 'uploading' ? 'determinate' : 'indeterminate'"
          [value]="file().progress"
          [attr.aria-label]="statusLabel()" />
      }
    </span>
  `,
  styleUrls: ['./ai-chat-file-chip.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiChatFileChipComponent {
  readonly file = input.required<ChatFileVM>();
  /** Composer chips can be removed; chips on a sent message can't. */
  readonly removable = input(false);

  readonly remove = output<void>();
  readonly retry = output<void>();

  readonly typeLabel = computed(() => fileTypeLabel(this.file().type));
  readonly statusLabel = computed(() => chatFileStatusLabel(this.file()));
  readonly busy = computed(() =>
    ['uploading', 'scanning', 'reading'].includes(this.file().step)
  );
  /** Only from the composer: a sent message can't take a file back. */
  readonly canRetry = computed(
    () => this.removable() && this.file().failure === 'upload'
  );
}
